import { userAvatarBucketName } from '@/config/env';
import { GradientAvatar } from '@/shared/components';
import { type AvatarSize, messageLayoutTokens } from '@/shared/theme/message-layout';
import { cn } from '@/shared/utilities';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface MessageAvatarProps {
  peerId: string;
  src?: string;
  name?: string;
  className?: string;
  isCompact?: boolean;
  hideAvatar?: boolean;
  isOwn?: boolean;
  /** Configurable avatar size - overrides isCompact behavior */
  avatarSize?: AvatarSize;
}

const MessageLayoutAvatar = memo<MessageAvatarProps>(
  ({ peerId, src, name, className, isCompact, hideAvatar, isOwn, avatarSize }) => {
    const { t } = useTranslation();

    // Calculate pixel size from theme tokens
    const pixelSize = useMemo(() => {
      // If avatarSize is explicitly set, use it
      if (avatarSize) {
        return messageLayoutTokens.avatar.size[avatarSize];
      }
      // Otherwise fall back to isCompact behavior with new standards
      return isCompact ? messageLayoutTokens.avatar.size.small : messageLayoutTokens.avatar.size.medium;
    }, [avatarSize, isCompact]);

    if (isCompact && isOwn) return null;
    if (hideAvatar) {
      return <div className={className} style={{ width: pixelSize, height: pixelSize }} />;
    }

    return (
      <div className={cn('mex:flex mex:items-end', className)}>
        <GradientAvatar
          peerId={peerId}
          bucketName={userAvatarBucketName}
          className='mex:text-[13px]'
          sx={{ width: pixelSize, height: pixelSize, fontSize: pixelSize < 32 ? 8 : 13 }}
          src={src}
          name={name}
          aria-label={t('messages.layout.userAvatar', { name: name || t('messages.layout.userFallback') })}
          role='img'
        />
      </div>
    );
  }
);

MessageLayoutAvatar.displayName = 'MessageLayoutAvatar';
export { MessageLayoutAvatar };
